import type { QueryClient } from '@tanstack/react-query';
import { api } from './api';

export function eventRangeQueryKey(
  calendarId: string,
  from: Date,
  to: Date,
  joinToken?: string | null
) {
  return [
    'events',
    calendarId,
    from.toISOString(),
    to.toISOString(),
    joinToken ?? null,
  ] as const;
}

export function eventRangeQueryOptions(
  calendarId: string,
  from: Date,
  to: Date,
  joinToken?: string | null
) {
  return {
    queryKey: eventRangeQueryKey(calendarId, from, to, joinToken),
    queryFn: () =>
      api.events.list(calendarId, {
        from: from.toISOString(),
        to: to.toISOString(),
        join_token: joinToken ?? undefined,
      }),
  };
}

function monthRange(month: Date): { from: Date; to: Date } {
  return {
    from: new Date(month.getFullYear(), month.getMonth(), 1),
    to: new Date(
      month.getFullYear(),
      month.getMonth() + 1,
      0,
      23,
      59,
      59
    ),
  };
}

export async function prefetchEventWindow(
  queryClient: QueryClient,
  calendarIds: string[],
  centerMonth = new Date(),
  monthsEachSide = 12,
  concurrency = 3
): Promise<void> {
  const requests = calendarIds.flatMap(calendarId =>
    Array.from(
      { length: monthsEachSide * 2 + 1 },
      (_, offset) => {
        const month = new Date(
          centerMonth.getFullYear(),
          centerMonth.getMonth() + offset - monthsEachSide,
          1
        );
        const { from, to } = monthRange(month);
        return () =>
          queryClient.prefetchQuery(
            eventRangeQueryOptions(calendarId, from, to)
          );
      }
    )
  );

  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < requests.length) {
      const request = requests[nextIndex++];
      await request();
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, requests.length) }, worker)
  );
}
